import { DATE_FORMAT } from 'common/constants/dateFormatConstants'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import {
  PATIENT_STATUSES,
  PATIENT_STATUS_NAMES,
  SMS_STATUSES,
} from 'features/Home/constants/infoConstants'
import { useFetchScheduleWeekQuery } from 'features/Schedule/api/schedule.api'
import { scheduleGridQueryOptions } from 'features/Schedule/api/scheduleQueryOptions'
import { nanoid } from 'nanoid'
import { useMemo } from 'react'
import { useParams } from 'react-router'
import {
  filterCanceledSchedules,
  sortCanceledSchedules,
} from 'features/Schedule/pages/components/utils'
import { filterSchedulesBySelectedStaff } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/scheduleInstance'

dayjs.extend(customParseFormat)

export const useScheduleWeekData = (
  date: string,
  selectedConsultationStaffIds: string[] | null,
  showCanceled = true,
) => {
  const { siteId } = useParams()

  const {
    currentData: scheduleWeek,
    isFetching: isFetchingWeek,
    isLoading: isLoadingWeek,
    isError,
  } = useFetchScheduleWeekQuery(
    {
      siteId,
      startDate: dayjs(date).startOf('week').format(DATE_FORMAT.PATIENT_TRACKER_DATE),
      endDate: dayjs(date).endOf('week').format(DATE_FORMAT.PATIENT_TRACKER_DATE),
    },
    {
      skip: !date || !siteId,
      ...scheduleGridQueryOptions,
    },
  )

  const consultationStaffs = useMemo(() => {
    if (!scheduleWeek) return []
    return scheduleWeek.consultation_staffs
  }, [JSON.stringify(scheduleWeek?.consultation_staffs)])

  const languages = useMemo(() => {
    if (!scheduleWeek) return []
    return scheduleWeek.languages
  }, [JSON.stringify(scheduleWeek?.languages)])

  const timeInterval = useMemo(() => {
    if (!scheduleWeek) return { min: '', max: '' }

    const formatTime = (time: string) => {
      const [hours, minutes] = time.split(':').map((item) => parseInt(item, 10))
      const formattedHours = hours.toString().padStart(2, '0')
      const formattedMinutes = minutes.toString().padStart(2, '0')
      return `${formattedHours}:${formattedMinutes}`
    }

    let globalMin: string | null = null
    let globalMax: string | null = null

    Object.values(scheduleWeek.time_interval.days).forEach((dayInterval) => {
      if (dayInterval.min && dayInterval.max) {
        const minTime = formatTime(dayInterval.min)
        const maxTime = formatTime(dayInterval.max)

        if (!globalMin || minTime < globalMin) {
          globalMin = minTime
        }
        if (!globalMax || maxTime > globalMax) {
          globalMax = maxTime
        }
      }
    })

    return {
      min: globalMin || '',
      max: globalMax || '',
    }
  }, [JSON.stringify(scheduleWeek?.time_interval)])

  const defaultAppointmentTime = useMemo(() => {
    if (!scheduleWeek) return 0
    return scheduleWeek.time_interval.default_appointment_time
  }, [JSON.stringify(scheduleWeek?.time_interval)])

  const disabledIntervals = useMemo(() => {
    if (!scheduleWeek) return []

    const formatTime = (time: string) => {
      const [hours, minutes] = time.split(':').map((item) => parseInt(item, 10))
      const formattedHours = hours.toString().padStart(2, '0')
      const formattedMinutes = minutes.toString().padStart(2, '0')
      return `${formattedHours}:${formattedMinutes}`
    }

    const intervals = Object.entries(scheduleWeek.days)
      .map(([dayKey, dayData]) => {
        const timeSlots = dayData.time_slots
        const dayTimeInterval = scheduleWeek.time_interval.days[dayKey]

        // If day has no min/max times, mark entire day as disabled
        if (!dayTimeInterval || !dayTimeInterval.min || !dayTimeInterval.max) {
          return [
            {
              id: nanoid(),
              name: dayKey,
              start: dayjs(`${dayKey} 00:00`, 'YYYY-MM-DD HH:mm').toDate(),
              end: dayjs(`${dayKey} 23:59`, 'YYYY-MM-DD HH:mm').toDate(),
              resourceId: dayKey,
              status: {
                code: PATIENT_STATUSES.DISABLED,
                name: PATIENT_STATUS_NAMES[PATIENT_STATUSES.DISABLED],
              },
              isDraggable: false,
            },
          ]
        }

        const minHour = formatTime(dayTimeInterval.min)
        const maxHour = formatTime(dayTimeInterval.max)

        const slotsWithDayjs = timeSlots.map((slot) => ({
          ...slot,
          start_time: dayjs(`${dayKey} ${slot.start_time}`, 'YYYY-MM-DD HH:mm:ss'),
          end_time: dayjs(`${dayKey} ${slot.end_time}`, 'YYYY-MM-DD HH:mm:ss'),
        }))

        if (!slotsWithDayjs.length)
          return [
            {
              id: nanoid(),
              name: dayKey,
              start: dayjs(`${dayKey} ${minHour}`, 'YYYY-MM-DD HH:mm').toDate(),
              end: dayjs(`${dayKey} ${maxHour}`, 'YYYY-MM-DD HH:mm').toDate(),
              resourceId: dayKey,
              status: {
                code: PATIENT_STATUSES.DISABLED,
                name: PATIENT_STATUS_NAMES[PATIENT_STATUSES.DISABLED],
              },
              isDraggable: false,
            },
          ]

        const getEndDate = (index: number) => {
          if (slotsWithDayjs[index + 1]?.start_time) {
            return slotsWithDayjs[index + 1].start_time.toDate()
          }

          return dayjs(`${dayKey} ${maxHour}`, 'YYYY-MM-DD HH:mm').toDate()
        }

        const slotsDisabledIntervals = slotsWithDayjs.map((slot, index) => ({
          id: nanoid(),
          name: dayKey,
          start: slot.end_time.toDate(),
          end: getEndDate(index),
          resourceId: dayKey,
          status: {
            code: PATIENT_STATUSES.DISABLED,
            name: PATIENT_STATUS_NAMES[PATIENT_STATUSES.DISABLED],
          },
          isDraggable: false,
        }))

        if (
          slotsWithDayjs[0] &&
          slotsWithDayjs[0].start_time.isAfter(dayjs(`${dayKey} ${minHour}`, 'YYYY-MM-DD HH:mm'))
        ) {
          slotsDisabledIntervals.unshift({
            id: nanoid(),
            name: dayKey,
            start: dayjs(`${dayKey} ${minHour}`, 'YYYY-MM-DD HH:mm').toDate(),
            end: slotsWithDayjs[0].start_time.toDate(),
            resourceId: dayKey,
            status: {
              code: PATIENT_STATUSES.DISABLED,
              name: PATIENT_STATUS_NAMES[PATIENT_STATUSES.DISABLED],
            },
            isDraggable: false,
          })
        }

        if (
          slotsDisabledIntervals.length > 0 &&
          dayjs(slotsDisabledIntervals[slotsDisabledIntervals.length - 1].start).isSame(
            dayjs(slotsDisabledIntervals[slotsDisabledIntervals.length - 1].end),
            'minute',
          )
        ) {
          slotsDisabledIntervals.pop()
        }

        return slotsDisabledIntervals
      })
      .flat()

    return intervals
  }, [JSON.stringify(scheduleWeek?.days), JSON.stringify(scheduleWeek?.time_interval)])

  const schedules = useMemo(() => {
    if (!scheduleWeek) return []

    const staffFilteredItems = filterSchedulesBySelectedStaff(
      scheduleWeek.items,
      selectedConsultationStaffIds,
    )

    return sortCanceledSchedules(filterCanceledSchedules(staffFilteredItems, showCanceled))
  }, [JSON.stringify(scheduleWeek?.items), selectedConsultationStaffIds, showCanceled])

  const eventsToSendNotification = useMemo(() => {
    if (!scheduleWeek) return []
    return schedules
      .filter(
        (item) =>
          item.sms_notification === SMS_STATUSES.UNCONFIRMED &&
          item.status.code === PATIENT_STATUSES.UNCONFIRMED,
      )
      .map((item) => item.id)
  }, [JSON.stringify(schedules)])

  const patientStatuses = useMemo(() => {
    if (!scheduleWeek) return []
    return scheduleWeek.patient_statuses
  }, [JSON.stringify(scheduleWeek?.patient_statuses)])

  const categoryStatuses = useMemo(() => {
    if (!scheduleWeek) return []
    return scheduleWeek.appointment_categories
  }, [JSON.stringify(scheduleWeek?.appointment_categories)])

  const doctorBufferTime = useMemo(() => {
    if (!scheduleWeek) return { before: null, after: null }
    return scheduleWeek.doctor_buffer_time
  }, [JSON.stringify(scheduleWeek?.doctor_buffer_time)])

  const appointmentCategories = useMemo(() => {
    if (!scheduleWeek) return []
    return scheduleWeek.appointment_categories
  }, [JSON.stringify(scheduleWeek?.appointment_categories)])

  const hasEhrBooking = useMemo(() => {
    return !!scheduleWeek?.has_ehr_booking
  }, [JSON.stringify(scheduleWeek?.has_ehr_booking)])

  const byPassTemperatureCheck = useMemo(() => {
    return scheduleWeek?.pandemic_protocol === 0
  }, [JSON.stringify(scheduleWeek?.pandemic_protocol)])

  const weekDays = useMemo(() => {
    if (!scheduleWeek) return {}
    return scheduleWeek.days
  }, [JSON.stringify(scheduleWeek?.days)])

  const rooms = useMemo(() => {
    if (!scheduleWeek) return []
    return scheduleWeek.rooms
  }, [JSON.stringify(scheduleWeek?.rooms)])

  const appointmentColor = useMemo(() => {
    if (!scheduleWeek) return 'status'
    return scheduleWeek.appointment_color
  }, [JSON.stringify(scheduleWeek?.appointment_color)])

  const punctuality = useMemo(() => {
    if (!scheduleWeek) return []
    return scheduleWeek.punctuality
  }, [JSON.stringify(scheduleWeek?.punctuality)])

  const patientBufferTime = useMemo(() => {
    if (!scheduleWeek) return { after: 0, before: 0 }
    return scheduleWeek.patient_buffer_time
  }, [JSON.stringify(scheduleWeek?.patient_buffer_time)])

  return {
    items: scheduleWeek?.items,
    isError,
    schedules,
    consultationStaffs,
    languages,
    timeInterval,
    disabledIntervals,
    weekDays,
    isFetchingWeek,
    isLoadingWeek,
    eventsToSendNotification,
    doctorBufferTime,
    defaultAppointmentTime,
    patientStatuses,
    appointmentCategories,
    hasEhrBooking,
    byPassTemperatureCheck,
    rooms,
    appointmentColor,
    categoryStatuses,
    punctuality,
    patientBufferTime,
  }
}
